/**
 * <dye-lot-picker>
 *
 * Renders the "Dye Lot" variant option as radio buttons and, when an order
 * quantity exceeds a single lot's stock, automatically splits the order across
 * multiple dye lots of the same colour — adding them as separate line items in
 * one /cart/add.js request.
 *
 * Architecture (mirrors the theme's colour-swatch pattern):
 *   - The visible control is a group of radio buttons (one per lot value).
 *   - A hidden <select name="options[Dye Lot]" id="option2" class="dynamic-variant-input">
 *     remains the *real* option control that ProductForm + VariantAvailability read.
 *     The radios drive it: on selection we set its value and dispatch `change`.
 *   - The element lives inside `.product__variants-wrapper` (which holds the colour
 *     swatches + the per-variant stock JSON). The product <form>, the visible
 *     quantity input and the add-to-cart buttons live OUTSIDE the wrapper, under the
 *     shared `.product-blocks` / `.product` section root — so we locate them there,
 *     NOT via `this.closest('form')`.
 *
 * Allocation rules (confirmed with the merchant):
 *   - Auto primary = smallest single lot whose stock >= quantity (single lot whenever
 *     possible); only combine when no single lot is big enough.
 *   - When combining, fill from the largest lots first (fewest lots → least colour
 *     variation), starting from the chosen primary.
 *   - Over-order (qty > colour's total stock): cap qty to that total, add all lots,
 *     show the "capped" notice.
 *   - Manual override: clicking a lot makes it the primary; it still auto-splits if the
 *     quantity exceeds it. The manual choice persists across quantity changes and is
 *     cleared when the colour changes.
 */
(function () {
  if (typeof window === 'undefined' || typeof customElements === 'undefined') return;
  if (customElements.get('dye-lot-picker')) return;

  var COLOUR_INDEX = 'option1';

  function toInt(value) {
    var n = Math.floor(Number(value));
    if (isNaN(n) || n < 1) return 1;
    return n;
  }

  function sumQty(rows) {
    return rows.reduce(function (total, r) { return total + (r.qty || 0); }, 0);
  }

  var DyeLotPicker = (function () {
    function DyeLotPicker() {
      // Reflect-based super() so this works as a native custom element.
      return Reflect.construct(HTMLElement, [], DyeLotPicker);
    }

    DyeLotPicker.prototype = Object.create(HTMLElement.prototype);
    DyeLotPicker.prototype.constructor = DyeLotPicker;

    DyeLotPicker.prototype.connectedCallback = function () {
      this._cleanup = [];
      this.manualLot = null;
      this.lastAllocations = [];
      this._suppressRadioRun = false;
      this._internalQtyChange = false;

      this.group = this.querySelector('[data-dye-lot-group]');
      if (!this.group) return; // product has no dye-lot option — no-op

      // Collapse the picker by default; a click on the prompt reveals it (one-way).
      // Done in JS so no-JS users still see the picker (progressive enhancement).
      this.revealBtn = this.querySelector('[data-dye-lot-reveal]');
      if (this.revealBtn) this.classList.add('is-collapsed');

      this.wrapper = this.closest('.product__variants-wrapper') || this;
      this.root =
        this.closest('.product-blocks') ||
        this.closest('[data-section-id]') ||
        this.closest('.product') ||
        document;

      this.select = this.querySelector('select[data-dye-lot-select]') || this.querySelector('select');
      this.radios = Array.prototype.slice.call(this.querySelectorAll('[data-dye-lot-radio]'));
      this.multiNotice = this.querySelector('[data-dye-lot-multi-notice]');
      this.cappedNotice = this.querySelector('[data-dye-lot-capped-notice]');
      this.cappedText = this.querySelector('[data-dye-lot-capped-text]');

      this.form = this.root.querySelector ? this.root.querySelector('form[data-product-form]') : null;
      this.qtyInput = this.root.querySelector
        ? this.root.querySelector('[data-quantity-input][id^="quantity-input-product-form"]')
        : null;

      this.stock = this.readStock();
      this.requestedQty = this.qtyInput ? toInt(this.qtyInput.value) : 1;
      this.dyeIndex = this.getAttribute('data-dye-lot-index') || 'option2';
      this.lastColour = null;

      this.bind();
      this.scheduleInitialRun();
    };

    DyeLotPicker.prototype.disconnectedCallback = function () {
      (this._cleanup || []).forEach(function (fn) { fn(); });
      this._cleanup = [];
    };

    /* ---------- helpers ---------- */

    DyeLotPicker.prototype.on = function (target, type, handler, opts) {
      if (!target) return;
      target.addEventListener(type, handler, opts);
      this._cleanup.push(function () { target.removeEventListener(type, handler, opts); });
    };

    DyeLotPicker.prototype.debounce = function (fn, wait) {
      var timer;
      return function () {
        var ctx = this;
        var args = arguments;
        clearTimeout(timer);
        timer = setTimeout(function () { fn.apply(ctx, args); }, wait);
      };
    };

    DyeLotPicker.prototype.readStock = function () {
      var el = this.wrapper.querySelector('[data-variant-stock]');
      if (!el) return [];
      try { return JSON.parse(el.textContent || '[]'); } catch (e) { return []; }
    };

    DyeLotPicker.prototype.currentColour = function () {
      var sel = this.wrapper.querySelector('#' + COLOUR_INDEX);
      if (sel && sel.value) return sel.value;
      var btn = this.wrapper.querySelector('[data-product-option="option1"] .product__color-swatch.selected');
      if (btn && btn.getAttribute('data-option-value')) return btn.getAttribute('data-option-value');
      return this.stock.length ? this.stock[0].c : null;
    };

    // In-stock, tracked lots for a colour.
    DyeLotPicker.prototype.lotsForColour = function (colour) {
      return this.stock.filter(function (r) {
        return r.c === colour && r.avail === true && r.qty !== null && r.qty > 0;
      });
    };

    DyeLotPicker.prototype.toggle = function (el, show) {
      if (el) el.classList.toggle('hidden', !show);
    };

    DyeLotPicker.prototype.addButtonEls = function () {
      if (!this.root || !this.root.querySelectorAll) return [];
      return Array.prototype.slice.call(this.root.querySelectorAll('[data-add-to-cart]'));
    };

    /* ---------- binding ---------- */

    DyeLotPicker.prototype.bind = function () {
      var self = this;

      // Reveal the picker on prompt click (one-way — no re-collapse). Auto-selection
      // keeps running while collapsed, so this is purely a visual reveal.
      if (this.revealBtn) {
        this.on(this.revealBtn, 'click', function () {
          self.classList.remove('is-collapsed');
          self.revealBtn.setAttribute('aria-expanded', 'true');
          var firstRadio = self.radios[0];
          if (firstRadio) firstRadio.focus();
        });
      }

      // Colour change (hidden #option1 select updated by the swatch buttons).
      this.on(this.wrapper, 'change', function (e) {
        if (e.target && e.target.id === COLOUR_INDEX) {
          self.manualLot = null;
          self.requestedQty = self.qtyInput ? toInt(self.qtyInput.value) : 1;
          self.run();
        }
      });

      // Colour swatch click — the select updates synchronously, but defer to be safe.
      this.on(this.wrapper, 'click', function (e) {
        if (e.target.closest && e.target.closest('[data-product-option="option1"] [data-button]')) {
          self.manualLot = null;
          setTimeout(function () {
            self.requestedQty = self.qtyInput ? toInt(self.qtyInput.value) : 1;
            self.run();
          }, 0);
        }
      });

      // Quantity changes (visible input — has the +/- buttons). Both `input` and
      // `change` funnel through one handler; we track the timer so an internal
      // clamp can cancel a pending user-input debounce (avoids losing the cap state).
      if (this.qtyInput) {
        var handleUserQty = function () {
          if (self._internalQtyChange) { self._internalQtyChange = false; return; }
          self.requestedQty = toInt(self.qtyInput.value);
          self.run();
        };
        this.on(this.qtyInput, 'change', handleUserQty);
        this.on(this.qtyInput, 'input', function () {
          clearTimeout(self._qtyTimer);
          self._qtyTimer = setTimeout(handleUserQty, 150);
        });
      }

      // Manual lot override.
      this.radios.forEach(function (radio) {
        self.on(radio, 'change', function () {
          if (self._suppressRadioRun) return;
          if (radio.checked) {
            self.manualLot = radio.value;
            self.run();
          }
        });
      });

      // Intercept submit in the capture phase so we pre-empt ProductForm's
      // bubble-phase submit handler when an order spans multiple lots.
      if (this.form) {
        this._onSubmit = this.onSubmit.bind(this);
        this.form.addEventListener('submit', this._onSubmit, true);
        this._cleanup.push(function () {
          self.form.removeEventListener('submit', self._onSubmit, true);
        });
      }

      // Re-run on theme/section events.
      var rerun = function () { self.run(); };
      this.on(document, 'product:variant-change', rerun);
      this.on(document, 'theme:variant:change', rerun);
      this.on(document, 'shopify:section:load', rerun);
    };

    DyeLotPicker.prototype.scheduleInitialRun = function () {
      var self = this;
      var attempts = 0;
      var max = 5;
      var tick = function () {
        self.run();
        attempts++;
        if (attempts < max) setTimeout(tick, 80);
      };
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(tick);
      } else {
        tick();
      }
    };

    /* ---------- core ---------- */

    DyeLotPicker.prototype.run = function () {
      if (!this.group) return;

      var colour = this.currentColour();
      var colourChanged = colour !== this.lastColour;
      this.lastColour = colour;
      var lots = this.lotsForColour(colour);

      this.filterRadios(lots);

      // Colour fully out of stock — let the theme disable the ATC button.
      if (!lots.length) {
        var colourRows = this.stock.filter(function (r) { return r.c === colour; });
        this.lastAllocations = [];
        this.clearUI();
        this.toggle(this.multiNotice, false);
        this.toggle(this.cappedNotice, false);
        // No in-stock lot for this colour. Still point the hidden option select at one of
        // THIS colour's real lots so the theme resolves the matching (sold-out) variant and
        // the button reads "Sold out". Without this the select keeps the previously selected
        // colour's lot, so `colour + staleLot` matches no variant and the button wrongly
        // reads "Unavailable" (and inconsistently "Sold out" when the stale lot happens to
        // exist for this colour) depending on which colour was selected before.
        if (colourRows.length) this.ensureChecked(colourRows[0].lot, colourChanged);
        return;
      }

      var requested = (this.requestedQty != null)
        ? this.requestedQty
        : (this.qtyInput ? toInt(this.qtyInput.value) : 1);
      var totalAvail = sumQty(lots);

      var capped = false;
      var qty = requested;
      var allocations;
      var primaryLot;

      if (requested > totalAvail) {
        capped = true;
        qty = totalAvail;
        this.setQty(qty);
        var allLots = lots.slice().sort(function (a, b) { return b.qty - a.qty; });
        allocations = allLots.map(function (r) { return { id: r.id, quantity: r.qty, lot: r.lot }; });
        primaryLot = allLots[0].lot;
      } else {
        // Resolve the manual override (cleared if it no longer fits this colour).
        var manualRow = null;
        var manualLot = this.manualLot;
        if (manualLot) {
          manualRow = lots.filter(function (r) { return r.lot === manualLot; })[0] || null;
          if (!manualRow) this.manualLot = null;
        }

        if (manualRow) {
          primaryLot = manualRow.lot;
          allocations = this.fill(manualRow, lots, qty);
        } else {
          var fits = lots.filter(function (r) { return r.qty >= qty; });
          if (fits.length) {
            fits.sort(function (a, b) { return a.qty - b.qty; });
            primaryLot = fits[0].lot;
            allocations = [{ id: fits[0].id, quantity: qty, lot: fits[0].lot }];
          } else {
            var largest = lots.slice().sort(function (a, b) { return b.qty - a.qty; })[0];
            primaryLot = largest.lot;
            allocations = this.fill(largest, lots, qty);
          }
        }
      }

      this.lastAllocations = allocations;

      this.ensureChecked(primaryLot, colourChanged);
      this.applyAllocationUI(allocations);

      // Both notices are independent: the multi-lot warning shows whenever the order
      // spans >1 lot, and the capped notice shows whenever the quantity was clamped —
      // so an over-order that also spans multiple lots shows both at once.
      var isMulti = allocations.length > 1;
      this.toggle(this.multiNotice, isMulti);
      this.toggle(this.cappedNotice, capped);
      if (capped && this.cappedText) {
        this.cappedText.textContent = this.cappedMessage(totalAvail);
      }
    };

    // Allocate from `primaryRow` first, then fill the remainder from the other
    // lots largest-first (fewest lots).
    DyeLotPicker.prototype.fill = function (primaryRow, lots, qty) {
      var take = Math.min(primaryRow.qty, qty);
      var allocations = [{ id: primaryRow.id, quantity: take, lot: primaryRow.lot }];
      var remaining = qty - take;
      if (remaining > 0) {
        var others = lots
          .filter(function (r) { return r.lot !== primaryRow.lot; })
          .sort(function (a, b) { return b.qty - a.qty; });
        for (var i = 0; i < others.length && remaining > 0; i++) {
          var t = Math.min(others[i].qty, remaining);
          if (t > 0) {
            allocations.push({ id: others[i].id, quantity: t, lot: others[i].lot });
            remaining -= t;
          }
        }
      }
      return allocations;
    };

    /* ---------- UI ---------- */

    // Show only the current colour's in-stock lots; hide & disable the rest.
    DyeLotPicker.prototype.filterRadios = function (lots) {
      var visible = {};
      lots.forEach(function (r) { visible[r.lot] = true; });
      this.radios.forEach(function (radio) {
        var label = radio.closest('[data-dye-lot]') || radio.parentElement;
        var show = !!visible[radio.value];
        if (label) label.classList.toggle('hidden', !show);
        radio.disabled = !show;
        if (!show) radio.checked = false;
      });
    };

    DyeLotPicker.prototype.ensureChecked = function (lot, colourChanged) {
      this._suppressRadioRun = true;
      this.radios.forEach(function (radio) {
        radio.checked = radio.value === lot && !radio.disabled;
      });
      this._suppressRadioRun = false;

      // Keep the "Dye Lot: X" label in sync ourselves (the theme also sets it on a
      // `change`, but we skip dispatching for same-colour lot changes — see below).
      if (lot != null) {
        var lbl = this.querySelector('[data-selected-value-for-option][data-option-name="' + this.dyeIndex + '"]');
        if (lbl) lbl.textContent = lot;
      }

      // Sync the hidden select (the real option control ProductForm reads). Only fire
      // `change` when the COLOUR changed and the value actually changed — i.e. when the
      // theme genuinely needs to re-resolve the variant + image. Same-colour lot changes
      // never change the image (lots share the colour's media), so skipping the event
      // avoids needlessly invoking the theme's image swap (which has a pre-existing
      // null-guard bug). ProductForm.getVariant() reads the select value live at submit,
      // so the variant still resolves correctly without the event.
      if (this.select && lot != null) {
        var changed = this.select.value !== lot;
        this.select.value = lot;
        if (changed && colourChanged) {
          this.select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    };

    DyeLotPicker.prototype.applyAllocationUI = function (allocations) {
      var isMulti = allocations.length > 1;
      var byLot = {};
      allocations.forEach(function (a) { byLot[a.lot] = a.quantity; });

      this.radios.forEach(function (radio) {
        var label = radio.closest('[data-dye-lot]');
        var badge = label ? label.querySelector('[data-dye-lot-badge]') : null;
        var lotQty = byLot[radio.value];
        var included = lotQty != null;

        if (label) {
          label.classList.toggle('is-primary', radio.checked && included);
          label.classList.toggle('is-also-included', included && !radio.checked);
        }
        if (badge) {
          if (included && isMulti) {
            badge.textContent = '× ' + lotQty; // "× N"
            badge.classList.remove('hidden');
          } else {
            badge.textContent = '';
            badge.classList.add('hidden');
          }
        }
      });
    };

    DyeLotPicker.prototype.clearUI = function () {
      this.radios.forEach(function (radio) {
        var label = radio.closest('[data-dye-lot]');
        var badge = label ? label.querySelector('[data-dye-lot-badge]') : null;
        if (label) {
          label.classList.remove('is-primary', 'is-also-included');
        }
        if (badge) {
          badge.textContent = '';
          badge.classList.add('hidden');
        }
      });
    };

    DyeLotPicker.prototype.cappedMessage = function (totalAvail) {
      var template = this.cappedText ? this.cappedText.getAttribute('data-dye-lot-capped-template') : '';
      if (template) {
        // The Liquid `t | escape` chain can leave entities (e.g. &#39;) in the
        // attribute value; decode them since we render via textContent, not HTML.
        if (template.indexOf('&') !== -1 && typeof DOMParser !== 'undefined') {
          var doc = new DOMParser().parseFromString(template, 'text/html');
          if (doc && doc.body) template = doc.body.textContent;
        }
        return template.replace(/\[\[\s*count\s*\]\]|\{\{\s*count\s*\}\}/gi, totalAvail);
      }
      return 'Only ' + totalAvail + ' available for this colour. You’ve reached the maximum quantity.';
    };

    DyeLotPicker.prototype.setQty = function (qty) {
      if (!this.qtyInput) return;
      // Cancel any pending user-input debounce so it can't re-read the clamped value.
      clearTimeout(this._qtyTimer);
      if (String(this.qtyInput.value) === String(qty)) return;
      this.qtyInput.value = qty;
      // Dispatch change so ProductForm copies it into the hidden [name="quantity"].
      // Flag it so our own handler doesn't recurse.
      this._internalQtyChange = true;
      this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    };

    // Push the resolved quantity into the form's [name="quantity"] inputs so the
    // theme's native single-line add submits the right amount regardless of the
    // ProductForm change-sync timing.
    DyeLotPicker.prototype.syncFormQuantity = function (quantity) {
      if (!this.form) return;
      var inputs = this.form.querySelectorAll('[name="quantity"]');
      Array.prototype.forEach.call(inputs, function (input) { input.value = quantity; });
    };

    /* ---------- submit / cart add ---------- */

    DyeLotPicker.prototype.onSubmit = function (event) {
      if (!this.group) return;

      // Recompute against the current colour/quantity/manual state.
      this.run();

      var allocations = (this.lastAllocations || []).filter(function (a) {
        return a.id && a.quantity > 0;
      });

      // Single line — let the theme's native submit handle it, but make sure the
      // form's quantity matches the resolved (possibly capped) amount first.
      if (allocations.length <= 1) {
        if (allocations.length === 1) this.syncFormQuantity(allocations[0].quantity);
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      this.addMultiple(allocations);
    };

    DyeLotPicker.prototype.addMultiple = function (allocations) {
      var self = this;
      var items = allocations.map(function (a) { return { id: a.id, quantity: a.quantity }; });
      var buttons = this.addButtonEls();

      buttons.forEach(function (b) { b.classList.add('loading'); });
      this.clearError();
      if (typeof window.emit === 'function') {
        try { window.emit('cart:updating'); } catch (e) {}
      }

      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          return res.json().then(function (body) {
            return { ok: res.ok, status: res.status, body: body };
          });
        })
        .then(function (res) {
          if (!res.ok || (res.body && (res.body.status === 422 || res.body.status === '422'))) {
            self.handleAddError(res.body, buttons);
            return;
          }
          return self.refreshCart(res.body, buttons);
        })
        .catch(function () {
          buttons.forEach(function (b) { b.classList.remove('loading'); });
        });
    };

    // Replicate the theme's post-add behaviour (assets/theme.js addItem .then +
    // onFormSubmit): refresh cart data, then emit the same events the drawer listens to.
    DyeLotPicker.prototype.refreshCart = function (addedBody, buttons) {
      return fetch('/cart.js', { method: 'GET', credentials: 'include' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var addedItem = (addedBody && addedBody.items && addedBody.items[0]) || addedBody;

          if (typeof window.emit === 'function') {
            try {
              window.emit('cart:updated', { cart: cart });
              window.emit(['quick-cart:updated', 'quick-view:close', 'quick-add:close']);
              window.emit('quick-cart:open');
            } catch (e) {}
          }
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
          document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: addedItem } }));

          buttons.forEach(function (b) { b.classList.remove('loading'); });
        });
    };

    DyeLotPicker.prototype.handleAddError = function (body, buttons) {
      buttons.forEach(function (b) { b.classList.remove('loading'); });
      var msg = (body && (body.description || body.message)) || 'Could not add to cart.';
      document.dispatchEvent(new CustomEvent('cart:error', { detail: { errorMessage: msg } }));
      var err = this.root.querySelector ? this.root.querySelector('[data-quantity-error]') : null;
      if (err) {
        err.textContent = msg;
        err.classList.remove('hidden');
      }
    };

    DyeLotPicker.prototype.clearError = function () {
      var err = this.root.querySelector ? this.root.querySelector('[data-quantity-error]') : null;
      if (err) err.classList.add('hidden');
    };

    return DyeLotPicker;
  })();

  customElements.define('dye-lot-picker', DyeLotPicker);
})();
